/**
 * Redux Actions for images
 * @module services/images/redux/actions
 */
import { Map } from 'immutable'
import { DataStoreViewRecord } from '../../data'

export const CREATE_IMAGES = 'CREATE_IMAGES'
export const CREATE_IMAGE = 'CREATE_IMAGE'
export const DELETE_IMAGE = 'DELETE_IMAGE'
export const DROP_IMAGES = 'DROP_IMAGES'
export const SHOW_BULK_UPLOAD_SUPPORT_ERROR = 'SHOW_BULK_UPLOAD_SUPPORT_ERROR'
export const CLEAR_BULK_UPLOAD_SUPPORT_ERROR = 'CLEAR_BULK_UPLOAD_SUPPORT_ERROR'
export const SET_BULK_UPLOAD_BATCH_SUMMARY = 'SET_BULK_UPLOAD_BATCH_SUMMARY'

/**
 * Show a pasteable bulk-upload support error on the upload-bulk page
 * @param {Object} param0
 * @param {Object} param0.report Structured incident report
 */
export function showBulkUploadSupportError ({ report } = {}) {
  return {
    type: SHOW_BULK_UPLOAD_SUPPORT_ERROR,
    payload: { report },
  }
}

/**
 * Attach the outcome counts of a finished batch to the support snackbar
 * @param {Object} param0
 * @param {Object} param0.summary Batch summary counts
 */
export function setBulkUploadBatchSummary ({ summary } = {}) {
  return {
    type: SET_BULK_UPLOAD_BATCH_SUMMARY,
    payload: { summary },
  }
}

/**
 * Clear the bulk-upload support snackbar
 */
export function clearBulkUploadSupportError () {
  return {
    type: CLEAR_BULK_UPLOAD_SUPPORT_ERROR,
  }
}

export function dropImages ({ options, application, storeKey }) {
  if (!Map.isMap(application)) {
    throw new Error('The application parameter is required to be a Map')
  }
  if (!Map.isMap(options)) {
    throw new Error('The options parameter is required to be a Map')
  }

  return {
    type: DROP_IMAGES,
    payload: { options, application, storeKey },
  }
}

/**
 * Persist images in application state by storeKey and applicationKey
 * @param {Object} param0 The params
 * @param {String} param0.storeKey The store key with the data
 * @param {Map} param0.application The application state from the store to create
 * @param {String} param0.applicationKey The the key in the application state for the images
 */
export function createImages ({ storeKey, application, applicationKey } = {}) {
  if (!Map.isMap(application)) {
    throw new Error('The application parameter is required to be a Map')
  }
  if (!applicationKey) {
    throw new Error('The applicationKey parameter is required')
  }
  return {
    type: CREATE_IMAGES,
    payload: { storeKey, application, applicationKey },
  }
}

/**
 * Delete a single image in application state by storeKey and applicationKey
 * @param {Object} param0 The params
 * @param {String} param0.storeKey The store key with the data
 * @param {Map} param0.application The application state from the store to delete
 * @param {String} param0.applicationKey The the key in the application state for the images
 */
export function deleteImage ({
  storeKey,
  imageApplicationData,
  imageApplicationKey,
  applicationKey,
  imageId,
  imageView,
} = {}) {
  if (!Map.isMap(imageApplicationData)) {
    throw new Error('The imageApplicationData parameter is required to be a Map')
  }
  if (!(imageView instanceof DataStoreViewRecord)) {
    throw new Error('The imageView parameter is required to be a DataStoreViewRecord')
  }
  if (!imageApplicationKey) {
    throw new Error('The imageApplicationKey parameter is required')
  }
  if (!applicationKey) {
    throw new Error('The applicationKey parameter is required')
  }
  if (!imageId) {
    throw new Error('The imageId parameter is required')
  }
  return {
    type: DELETE_IMAGE,
    payload: {
      storeKey,
      imageApplicationData,
      imageApplicationKey,
      applicationKey,
      imageId,
      imageView,
    },
  }
}

/**
 * Create a single image in application state by storeKey and applicationKey
 * @param {Object} param0 The params
 * @param {String} param0.storeKey The store key with the data
 * @param {Map} param0.application The application state from the store to create
 * @param {String} param0.applicationKey The the key in the application state for the images
 */
export function createImage ({
  storeKey,
  imageApplicationData,
  imageApplicationKey,
  applicationKey,
  imageView,
} = {}) {
  if (!Map.isMap(imageApplicationData)) {
    throw new Error('The imageApplicationData parameter is required to be a Map')
  }
  if (imageView && !(imageView instanceof DataStoreViewRecord)) {
    throw new Error('The imageView parameter is required to be a DataStoreViewRecord')
  }
  if (!imageApplicationKey) {
    throw new Error('The imageApplicationKey parameter is required')
  }
  if (!applicationKey) {
    throw new Error('The applicationKey parameter is required')
  }
  return {
    type: CREATE_IMAGE,
    payload: {
      storeKey,
      imageApplicationData,
      imageApplicationKey,
      applicationKey,
      imageView,
    },
  }
}
